import { Box, ButtonBase, Divider, ListItemIcon, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { viewerMatches } from './navigation';
import { organizationsApi } from '@/api/resources';
import { MY_ORGANIZATION_KEY } from '@/features/myOrganization/keys';
import { myOrganizationModule } from '@/modules/myOrganization';
import { usePermissions } from '@/hooks/usePermissions';
import { StatusChip } from '@/components/common/StatusChip';
import { radii } from '@/theme/tokens';
import { endEdge } from './menuOrigin';


/**
 * Who you are acting as, and the one page about it.
 *
 * "My organization" is chrome, not a tile (owner's call, 2026-09-27), and this
 * menu is its home. The entries come from a manifest, gated by the same
 * `viewerMatches` as everything else, so the day it becomes a module the menu
 * goes and nothing else changes. Platform staff have no organization and see
 * no button at all.
 */
export function OrganizationMenu() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { permissions, isPlatform } = usePermissions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const allowed = viewerMatches(myOrganizationModule, { isPlatform, permissions });

  // Tenant only, and only the name: the shell never blocks on this, and the
  // page itself shares the cache entry.
  const me = useQuery({
    queryKey: MY_ORGANIZATION_KEY,
    queryFn: organizationsApi.me,
    enabled: allowed,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (!allowed) return null;

  /*
   * Not `localized()`: it answers an em dash when both names are null, which is
   * truthy, so a chain of fallbacks would stop at the dash rather than reach the
   * legal name. Same bug that shipped once in the prescriber block.
   */
  const preferArabic = i18n.language.startsWith('ar') || i18n.language.startsWith('ckb');
  const pick = (ar?: string | null, en?: string | null) =>
    (preferArabic ? ar || en : en || ar) || undefined;
  const name =
    pick(me.data?.tradeNameAr, me.data?.tradeNameEn) ??
    pick(me.data?.legalNameAr, me.data?.legalNameEn) ??
    t('nav.myOrganization');

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={t('nav.myOrganization')}
        sx={{
          height: 38,
          px: 1.25,
          gap: 1,
          minWidth: 0,
          borderRadius: `${radii.sm}px`,
          color: 'text.primary',
          fontFamily: 'inherit',
          bgcolor: anchor ? 'brand.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          '&.Mui-focusVisible': { boxShadow: (th) => `0 0 0 3px ${th.palette.brand.focusRing}` },
        }}
      >
        <ApartmentRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography
          noWrap
          component="span"
          sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.875rem', fontWeight: 500, maxWidth: 200 }}
        >
          {name}
        </Typography>
        <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: endEdge(theme.direction) }}
        transformOrigin={{ vertical: 'top', horizontal: endEdge(theme.direction) }}
        slotProps={{ paper: { sx: { width: 290, maxWidth: '92vw' } } }}
      >
        {/* Who you are acting as, with its status as a word (Shell board). */}
        <Box sx={{ px: 1.25, pt: 1.25, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.disabled' }}>
            {t('modules.actingAs')}
          </Typography>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              aria-hidden
              sx={{
                flex: '0 0 36px',
                height: 36,
                borderRadius: `${radii.md}px`,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'brand.accentTint',
                color: theme.palette.mode === 'light' ? 'brand.primary' : 'brand.primaryInk',
              }}
            >
              <ApartmentRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
              {name}
            </Typography>
          </Stack>
          {me.data?.status ? (
            <Box>
              <StatusChip value={me.data.status} namespace="orgStatus" size="small" />
            </Box>
          ) : null}
        </Box>
        <Divider sx={{ mx: 0.75, my: '4px !important' }} />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate(myOrganizationModule.landing({ isPlatform, permissions }));
          }}
          sx={{ color: 'brand.primaryInk', fontWeight: 600, '& .MuiListItemIcon-root': { color: 'brand.primaryInk' } }}
        >
          <ListItemIcon>
            <BusinessCenterRoundedIcon />
          </ListItemIcon>
          {t(myOrganizationModule.titleKey)}
        </MenuItem>
      </Menu>
    </>
  );
}
